import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { Box, ClickAwayListener, TextField } from '@mui/material'
import useSize from '@react-hook/size'
import PropTypes from 'prop-types'
import { useCanEdit, useFocusTrap } from '~/hooks'

const getSX = (type, variant) => {
  const sx = {
    lineHeight: '1.5',
  }
  switch (type) {
    case 'input':
      // sx.height = '24px'
      break
    case 'read-only':
      sx.display = 'block'
      sx.overflow = 'clip'
      sx.textOverflow = 'ellipsis'
      sx.whiteSpace = 'nowrap'
      if (variant === 'header') {
        sx.border = '1px solid transparent'
        sx.padding = '5px'
      } else {
        sx.border = (theme) =>
          theme.palette.mode === 'light'
            ? '1px solid rgba(0, 0, 0, 0.23)'
            : '1px solid rgba(255, 255, 255, 0.23)'
        sx.borderRadius = '4px'
        sx.padding = '5px 14px'
      }
      break
  }
  return sx
}

const EditableContext = createContext()

const actions = {
  ICONS_WIDTH: 'ICONS_WIDTH',
  VALUE: 'VALUE',
  VARIANT: 'VARIANT',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.ICONS_WIDTH:
      return { ...state, iconsWidth: action.value }
    case actions.VALUE:
      return { ...state, value: action.value }
    case actions.VARIANT:
      return { ...state, variant: action.value }
    default:
      return { ...state }
  }
}

const Input = (props) => {
  const { inputRef, placeholder, onCancel, onAccept, ...rest } = props

  const { reducerBag } = useContext(EditableContext)
  const [{ iconsWidth, value, variant }, dispatch] = reducerBag

  const ref = useRef()
  useFocusTrap(ref)

  const onKeyDown = useCallback(
    (e) => {
      switch (e.code) {
        case 'Tab':
        case 'Enter': {
          e.target.selectionStart = e.target.selectionEnd
          onAccept(e.target.value)
          break
        }
        case 'Escape':
          e.target.selectionStart = e.target.selectionEnd
          onCancel()
          e.stopPropagation()
          break
      }
    },
    [onCancel, onAccept]
  )

  useEffect(() => {
    inputRef.current.focus()
    inputRef.current.select()
  }, [inputRef])

  const sx = useMemo(() => {
    const sx = getSX('read-only', variant)
    switch (variant) {
      case 'name':
        if (iconsWidth) sx.paddingLeft = `${iconsWidth}px`
        sx.textAlign = 'right'
        break
      case 'value':
        if (iconsWidth) sx.paddingRight = `${iconsWidth}px`
        break
    }
    return sx
  }, [iconsWidth, variant])

  return (
    <ClickAwayListener onClickAway={onCancel}>
      <TextField
        {...rest}
        ref={ref}
        className="field"
        fullWidth
        hiddenLabel
        inputRef={inputRef}
        size="small"
        placeholder={placeholder}
        sx={{
          ...(props.sx ?? {}),
          '& .MuiInputBase-input': sx,
        }}
        value={value}
        onChange={(e) => {
          dispatch({ type: actions.VALUE, value: e.target.value })
          rest.onChange?.(e)
        }}
        onKeyDown={(e) => {
          if (rest.onKeyDown) {
            rest.onKeyDown(e)
          }
          onKeyDown(e)
        }}
      />
    </ClickAwayListener>
  )
}

Input.propTypes = {
  inputRef: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  value: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const ReadOnly = (props) => {
  const { as: Component, ...rest } = props
  const { reducerBag } = useContext(EditableContext)
  const [{ iconsWidth, value, variant }] = reducerBag

  const sx = useMemo(() => {
    const sx = getSX('read-only', variant)
    switch (variant) {
      case 'name':
        sx.backgroundColor = 'background.button'
        sx.borderColor = 'transparent'
        sx.fontWeight = 600
        if (iconsWidth) sx.paddingLeft = `${iconsWidth}px`
        sx.textAlign = 'right'
        break
    }
    return sx
  }, [iconsWidth, variant])

  const content = value || <>&nbsp;</>

  return (
    <Box {...rest} sx={sx}>
      {Component ? <Component>{content}</Component> : content}
    </Box>
  )
}

ReadOnly.propTypes = {
  as: PropTypes.elementType,
  sx: PropTypes.object,
  value: PropTypes.string,
}

const Editable = (props) => {
  const {
    children,
    disabled,
    resetable,
    slots = {},
    sx = {},
    variant,
    value: originalValue = '',
    onStore,
    ...rest
  } = props

  const reducerBag = useReducer(reducer, {
    value: originalValue || '',
    variant,
  })
  const [, dispatch] = reducerBag
  useEffect(() => {
    dispatch({ type: actions.VARIANT, value: variant })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant])

  const [editing, setEditing] = useState(false)

  const propPlaceholder = props.placeholder ?? ''
  const [hidePlaceholderAfterAccept, setHidePlaceholderAfterAccept] =
    useState(false)

  useEffect(() => {
    queueMicrotask(() => {
      setHidePlaceholderAfterAccept(false)
    })
  }, [propPlaceholder])

  const placeholder = hidePlaceholderAfterAccept ? '' : propPlaceholder

  useEffect(() => {
    dispatch({ type: actions.VALUE, value: originalValue || '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalValue])

  const canEdit = useCanEdit()
  const isEditable = useMemo(
    () => canEdit && !disabled && onStore,
    [canEdit, disabled, onStore]
  )

  const switchMode = useCallback(() => {
    if (!isEditable) return
    setEditing((e) => !e)
  }, [isEditable])

  const inputRef = useRef()

  const onAccept = useCallback(
    async (value) => {
      let success = true
      if (originalValue !== value) {
        success = await onStore?.(value)
        if (!success) {
          inputRef.current.focus()
          inputRef.current.select()
        }
      }
      if (!resetable) {
        setHidePlaceholderAfterAccept(true)
      }
      if (success) {
        switchMode()
      }
    },
    [onStore, originalValue, resetable, switchMode]
  )

  const onCancel = useCallback(() => {
    switchMode()
    dispatch({ type: actions.VALUE, value: originalValue || '' })
  }, [dispatch, originalValue, switchMode])

  const iconsRef = useRef()
  const icons = useMemo(() => {
    if (!children) return
    const sx = {
      padding: '0 0.5rem',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
    }
    if (variant === 'value') {
      sx.right = 0
    }
    return (
      <Box ref={iconsRef} sx={sx}>
        {children}
      </Box>
    )
  }, [children, variant])

  const [iconsWidth] = useSize(iconsRef)
  useEffect(() => {
    dispatch({ type: actions.ICONS_WIDTH, value: iconsWidth })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconsWidth])

  return (
    <EditableContext.Provider value={{ reducerBag }}>
      <Box sx={{ ...sx, position: 'relative' }}>
        {editing ? (
          <Input
            {...rest}
            inputRef={inputRef}
            placeholder={placeholder}
            // sx={{ '.MuiInputBase-input': { height: '24px !important' } }}
            sx={{
              '.MuiOutlinedInput-root': {
                fontSize: 'inherit !important',
                fontWeight: 'inherit !important',
              },
              '.MuiInputBase-input': {
                height: '1.5em !important',
              },
            }}
            onAccept={onAccept}
            onCancel={onCancel}
          />
        ) : (
          <ReadOnly
            {...rest}
            as={slots.readOnly}
            onDoubleClick={() => {
              if (editing) return
              switchMode()
            }}
          />
        )}
        {icons}
      </Box>
    </EditableContext.Provider>
  )
}

Editable.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  resetable: PropTypes.bool,
  slots: PropTypes.shape({
    readOnly: PropTypes.elementType,
  }),
  sx: PropTypes.object,
  variant: PropTypes.oneOf(['name', 'value', 'header']),
  value: PropTypes.string,
  onStore: PropTypes.func,
}

export default Editable
